/**
 * Core domain types for Milestone 1: Read-only photo browsing
 * No dependencies on adapters or external libraries.
 */

export type PhotoId = string;

/**
 * Photo with metadata
 * Note: thumbnailUrl is a UI cache concern, populated lazily
 * Supports both photos and videos (optional video-specific fields)
 */
export interface Photo {
  readonly id: PhotoId;
  readonly filePath: string;
  readonly dateTaken: Date;
  readonly thumbnailUrl?: string;
  readonly fileSize: number;
  readonly mimeType: string;
  readonly isStarred: boolean;
  // Video-specific fields (optional, backwards compatible)
  readonly mediaType?: 'photo' | 'video';  // Discriminator (undefined = photo for backwards compat)
}

/**
 * Photo group metadata for grouping by month and year
 */
export interface PhotoGroup {
  readonly year: number;         // Full year (e.g., 2024)
  readonly month: number;        // Month (0-11, JavaScript Date convention)
  readonly startIndex: number;   // Index of first photo in this group
  readonly count: number;        // Number of photos in this group
}

/**
 * Type guard: Check if a Photo is a video
 */
export function isVideo(photo: Photo): boolean {
  return photo.mediaType === 'video';
}

/**
 * Type guard: Check if a Photo is a photo (image)
 * Treats undefined mediaType as photo for backwards compatibility
 */
export function isPhoto(photo: Photo): boolean {
  return photo.mediaType === 'photo' || !photo.mediaType;
}

/**
 * Pure function: Sort photos by date (newest first)
 */
export function sortPhotos(photos: ReadonlyArray<Photo>): ReadonlyArray<Photo> {
  const sorted = [...photos];
  sorted.sort((a, b) => {
    const diff = a.dateTaken.getTime() - b.dateTaken.getTime();
    return -diff; // Newest first
  });
  return sorted;
}

/**
 * Pure function: Group photos by month and year
 * Assumes photos are already sorted (newest first)
 * Returns groups in the same order as photos (newest first)
 * Uses UTC time consistently throughout the application
 */
export function groupPhotosByMonth(photos: ReadonlyArray<Photo>): PhotoGroup[] {
  if (photos.length === 0) {
    return [];
  }

  // Internal mutable version for efficient grouping (matches PhotoGroup structure)
  type MutablePhotoGroup = {
    year: number;
    month: number;
    startIndex: number;
    count: number;
  };

  const groups: MutablePhotoGroup[] = [];
  let currentGroup: MutablePhotoGroup | null = null;

  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];
    const year = photo.dateTaken.getUTCFullYear();
    const month = photo.dateTaken.getUTCMonth(); // 0-11

    // Check if we need to start a new group
    if (!currentGroup || currentGroup.year !== year || currentGroup.month !== month) {
      // Save previous group if it exists
      if (currentGroup) {
        groups.push(currentGroup);
      }

      // Start new group
      currentGroup = {
        year,
        month,
        startIndex: i,
        count: 1,
      };
    } else {
      // Continue current group
      currentGroup.count++;
    }
  }

  // Don't forget the last group
  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}
